/**
 * CoachPlaybook - Play Surface Renderer
 * Version: 0.6.0
 * Description: Broadcast-quality SVG rendering for all sport surfaces with textures,
 *              gradients, filters, and premium visual fidelity. ESPN studio floor meets
 *              next-gen sports game aesthetics.
 */

import React, { useMemo } from 'react';

// Surface color themes per sport
const SURFACE_THEMES = {
  basketball: {
    surface: '#c4833e',
    surfaceAlt: '#b5742f',
    lines: '#ffffff',
    key: '#d4935e',
    rim: '#ff6b35',
    outOfBounds: '#1a1a22'
  },
  soccer: {
    surface: '#2d8a4e',
    surfaceAlt: '#267a42',
    lines: '#ffffff',
    outOfBounds: '#1a1a22'
  },
  baseball: {
    infield: '#c4833e',
    outfield: '#2d8a4e',
    outfieldAlt: '#267a42',
    dirt: '#b07030',
    lines: '#ffffff',
    bases: '#ffffff',
    outOfBounds: '#1a1a22'
  },
  football: {
    surface: '#2d8a4e',
    surfaceAlt: '#267a42',
    lines: '#ffffff',
    endzone: '#1a5c30',
    numbers: 'rgba(255,255,255,0.5)',
    outOfBounds: '#1a1a22'
  },
  volleyball: {
    surfaceLeft: '#2563eb',
    surfaceRight: '#f97316',
    lines: '#ffffff',
    net: '#333333',
    attackLine: '#ef4444',
    outOfBounds: '#1a1a22'
  }
};

// Aspect ratios for each sport (width:height as rendered vertically — long axis is vertical)
const ASPECT_RATIOS = {
  basketball: { w: 100, h: 188 },      // 50ft wide x 94ft long
  basketballHalf: { w: 100, h: 100 },   // Half court is roughly square
  soccer: { w: 100, h: 154.4 },         // 68m wide x 105m long
  baseball: { w: 150, h: 110 },          // Wide field image + backstop/dugout area below
  football: { w: 100, h: 225 },          // 53.33yd wide x 120yd long
  volleyball: { w: 100, h: 200 }         // 9m wide x 18m long
};

// ===== SHARED SVG DEFS =====
const SharedDefs = ({ sport, vbW, vbH }) => (
  <defs>
    {/* Noise texture filter */}
    <filter id={`noise-${sport}`} x="0%" y="0%" width="100%" height="100%">
      <feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" result="noise" />
      <feColorMatrix type="saturate" values="0" in="noise" result="grayNoise" />
      <feBlend mode="overlay" in="SourceGraphic" in2="grayNoise" />
    </filter>

    {/* Line glow filter for broadcast-quality markings */}
    <filter id={`lineGlow-${sport}`} x="-20%" y="-20%" width="140%" height="140%">
      <feGaussianBlur in="SourceGraphic" stdDeviation="0.4" result="blur" />
      <feMerge>
        <feMergeNode in="blur" />
        <feMergeNode in="SourceGraphic" />
      </feMerge>
    </filter>

    {/* Strong line glow for key markings */}
    <filter id={`lineGlowStrong-${sport}`} x="-30%" y="-30%" width="160%" height="160%">
      <feGaussianBlur in="SourceGraphic" stdDeviation="0.7" result="blur" />
      <feMerge>
        <feMergeNode in="blur" />
        <feMergeNode in="blur" />
        <feMergeNode in="SourceGraphic" />
      </feMerge>
    </filter>

    {/* Vignette radial gradient */}
    <radialGradient id={`vignette-${sport}`} cx="50%" cy="50%" r="70%">
      <stop offset="0%" stopColor="transparent" />
      <stop offset="70%" stopColor="transparent" />
      <stop offset="100%" stopColor="rgba(0,0,0,0.35)" />
    </radialGradient>

    {/* Spotlight gradient (center brighter) */}
    <radialGradient id={`spotlight-${sport}`} cx="50%" cy="50%" r="55%">
      <stop offset="0%" stopColor="rgba(255,255,255,0.08)" />
      <stop offset="100%" stopColor="transparent" />
    </radialGradient>

    {/* Basketball hardwood plank pattern */}
    <pattern id="hardwood" x="0" y="0" width="100" height="2.5" patternUnits="userSpaceOnUse">
      <rect width="100" height="2.5" fill="#c4833e" />
      <line x1="0" y1="2.4" x2="100" y2="2.4" stroke="#a06828" strokeWidth="0.12" opacity="0.5" />
      <rect x="0" y="0" width="100" height="1.25" fill="#c8883f" opacity="0.15" />
    </pattern>

    {/* Basketball hardwood with grain detail */}
    <pattern id="hardwoodGrain" x="0" y="0" width="8" height="2.5" patternUnits="userSpaceOnUse">
      <line x1="1" y1="0" x2="1.2" y2="2.5" stroke="#a06828" strokeWidth="0.06" opacity="0.2" />
      <line x1="3.5" y1="0" x2="3.3" y2="2.5" stroke="#a06828" strokeWidth="0.04" opacity="0.15" />
      <line x1="5.8" y1="0" x2="6" y2="2.5" stroke="#a06828" strokeWidth="0.05" opacity="0.18" />
      <line x1="7.2" y1="0" x2="7" y2="2.5" stroke="#b07830" strokeWidth="0.04" opacity="0.12" />
    </pattern>

    {/* Key/paint wood tone */}
    <pattern id="hardwoodKey" x="0" y="0" width="100" height="2.5" patternUnits="userSpaceOnUse">
      <rect width="100" height="2.5" fill="#d4935e" />
      <line x1="0" y1="2.4" x2="100" y2="2.4" stroke="#b87840" strokeWidth="0.12" opacity="0.4" />
      <rect x="0" y="0" width="100" height="1.25" fill="#d89862" opacity="0.12" />
    </pattern>

    {/* Grass texture pattern (fine organic dots) */}
    <pattern id="grassTexture" x="0" y="0" width="6" height="6" patternUnits="userSpaceOnUse">
      <rect width="6" height="6" fill="transparent" />
      <circle cx="1" cy="1.2" r="0.15" fill="#1a6e38" opacity="0.3" />
      <circle cx="3.5" cy="0.8" r="0.12" fill="#3aa060" opacity="0.25" />
      <circle cx="5" cy="2.5" r="0.13" fill="#1a6e38" opacity="0.2" />
      <circle cx="0.8" cy="4" r="0.1" fill="#3aa060" opacity="0.22" />
      <circle cx="2.5" cy="3.2" r="0.14" fill="#1a6e38" opacity="0.28" />
      <circle cx="4.2" cy="5" r="0.11" fill="#3aa060" opacity="0.2" />
      <line x1="1.5" y1="1" x2="1.7" y2="0.3" stroke="#1a6e38" strokeWidth="0.08" opacity="0.2" />
      <line x1="4" y1="3.5" x2="3.8" y2="2.9" stroke="#3aa060" strokeWidth="0.07" opacity="0.18" />
    </pattern>

    {/* Dirt/infield granular texture */}
    <pattern id="dirtTexture" x="0" y="0" width="4" height="4" patternUnits="userSpaceOnUse">
      <rect width="4" height="4" fill="transparent" />
      <circle cx="0.8" cy="0.6" r="0.18" fill="#8a5520" opacity="0.3" />
      <circle cx="2.5" cy="1.2" r="0.12" fill="#a06828" opacity="0.25" />
      <circle cx="1.5" cy="2.8" r="0.15" fill="#8a5520" opacity="0.2" />
      <circle cx="3.2" cy="3.5" r="0.1" fill="#a06828" opacity="0.28" />
      <circle cx="0.5" cy="3.8" r="0.13" fill="#8a5520" opacity="0.22" />
      <circle cx="3.8" cy="0.8" r="0.14" fill="#a06828" opacity="0.18" />
      <circle cx="2" cy="2" r="0.09" fill="#704418" opacity="0.2" />
    </pattern>

    {/* Goal net crosshatch */}
    <pattern id="netPattern" x="0" y="0" width="1.2" height="1.2" patternUnits="userSpaceOnUse">
      <line x1="0" y1="0" x2="1.2" y2="1.2" stroke="#cccccc" strokeWidth="0.08" opacity="0.6" />
      <line x1="1.2" y1="0" x2="0" y2="1.2" stroke="#cccccc" strokeWidth="0.08" opacity="0.6" />
    </pattern>

    {/* Volleyball net crosshatch */}
    <pattern id="volleyNetPattern" x="0" y="0" width="2" height="2" patternUnits="userSpaceOnUse">
      <line x1="0" y1="0" x2="2" y2="2" stroke="#222222" strokeWidth="0.12" opacity="0.7" />
      <line x1="2" y1="0" x2="0" y2="2" stroke="#222222" strokeWidth="0.12" opacity="0.7" />
    </pattern>

    {/* Polished floor reflection pattern */}
    <pattern id="polishedFloor" x="0" y="0" width="100" height="4" patternUnits="userSpaceOnUse">
      <rect width="100" height="4" fill="transparent" />
      <line x1="0" y1="3.9" x2="100" y2="3.9" stroke="rgba(255,255,255,0.06)" strokeWidth="0.15" />
    </pattern>

    {/* Metallic rim gradient */}
    <linearGradient id="rimMetal" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stopColor="#ff8855" />
      <stop offset="30%" stopColor="#ff6b35" />
      <stop offset="60%" stopColor="#cc4411" />
      <stop offset="100%" stopColor="#ff7744" />
    </linearGradient>

    {/* Base shadow for 3D effect */}
    <filter id="baseShadow" x="-50%" y="-50%" width="200%" height="200%">
      <feDropShadow dx="0.15" dy="0.15" stdDeviation="0.2" floodColor="#000000" floodOpacity="0.5" />
    </filter>

    {/* Mound elevation gradient */}
    <radialGradient id="moundGrad" cx="50%" cy="45%" r="50%">
      <stop offset="0%" stopColor="#c88040" />
      <stop offset="60%" stopColor="#b07030" />
      <stop offset="100%" stopColor="#9a6028" />
    </radialGradient>

    {/* Endzone gradient */}
    <linearGradient id="endzoneGrad" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stopColor="#1a6e38" />
      <stop offset="50%" stopColor="#155c2e" />
      <stop offset="100%" stopColor="#0f4a24" />
    </linearGradient>
  </defs>
);

// ===== BASKETBALL HALF COURT =====
// Renders the v0.5.146 PNG court image (baseline at bottom, hoop near top ~y=10.5)
// so basketball's shot arc lands on the visible hoop. The line-drawn SVG surface
// introduced in v0.6.0 put the hoop at y=91.5 (opposite orientation), which broke
// the plugin's HOOP_POSITIONS coord math.
const BasketballHalf = () => (
  <g>
    <image
      href="/court-half.png"
      x="0" y="0" width="100" height="100"
      preserveAspectRatio="none"
    />
  </g>
);

// ===== BASKETBALL FULL COURT =====
// Same rationale as BasketballHalf — restore the PNG so shot animation coord math
// (fullTop y=4.5, fullBottom y=95.5 pre-scale) aligns with the visible hoops.
const BasketballFull = () => {
  const vb = ASPECT_RATIOS.basketball;
  return (
    <g>
      <image
        href="/court-full.png"
        x="0" y="0" width={vb.w} height={vb.h}
        preserveAspectRatio="none"
      />
    </g>
  );
};

// ===== SOCCER PITCH =====
const SoccerFull = ({ theme }) => {
  const vb = ASPECT_RATIOS.soccer;
  const s = 1.4706;
  const stripeCount = 14;
  const stripeH = (vb.h - 6) / stripeCount;

  return (
    <g>
      {/* PNG grass texture with mow stripes */}
      <image href="/textures/grass.png" x="0" y="0" width={vb.w} height={vb.h} preserveAspectRatio="none" />

      {/* Spotlight effect */}
      <rect x="0" y="0" width={vb.w} height={vb.h} fill="url(#spotlight-soccer)" />

      {/* Clean white lines — no shadow, no blur */}
      <rect x="3" y="3" width="94" height={vb.h - 6} fill="none" stroke={theme.lines} strokeWidth="0.7" />
      <line x1="3" y1={vb.h / 2} x2="97" y2={vb.h / 2} stroke={theme.lines} strokeWidth="0.7" />
      <circle cx="50" cy={vb.h / 2} r={9.15 * s} fill="none" stroke={theme.lines} strokeWidth="0.6" />
      <circle cx="50" cy={vb.h / 2} r="0.8" fill={theme.lines} />

      {/* Top penalty area */}
      <rect x={50 - 29.65} y="3" width={59.3} height={16.5 * s} fill="none" stroke={theme.lines} strokeWidth="0.6" />
      <rect x={50 - 13.47} y="3" width={26.94} height={5.5 * s} fill="none" stroke={theme.lines} strokeWidth="0.6" />
      <circle cx="50" cy={3 + 11 * s} r="0.6" fill={theme.lines} />
      <path d={`M ${50 - 10.77} ${3 + 16.5 * s} A ${9.15 * s} ${9.15 * s} 0 0 0 ${50 + 10.77} ${3 + 16.5 * s}`} fill="none" stroke={theme.lines} strokeWidth="0.6" />

      {/* Top goal */}
      <rect x={50 - 5.38} y="0.5" width={10.76} height="2.5" fill="url(#netPattern)" stroke={theme.lines} strokeWidth="0.8" />

      {/* Bottom penalty area (mirrored) */}
      <rect x={50 - 29.65} y={vb.h - 3 - 16.5 * s} width={59.3} height={16.5 * s} fill="none" stroke={theme.lines} strokeWidth="0.6" />
      <rect x={50 - 13.47} y={vb.h - 3 - 5.5 * s} width={26.94} height={5.5 * s} fill="none" stroke={theme.lines} strokeWidth="0.6" />
      <circle cx="50" cy={vb.h - 3 - 11 * s} r="0.6" fill={theme.lines} />
      <path d={`M ${50 - 10.77} ${vb.h - 3 - 16.5 * s} A ${9.15 * s} ${9.15 * s} 0 0 1 ${50 + 10.77} ${vb.h - 3 - 16.5 * s}`} fill="none" stroke={theme.lines} strokeWidth="0.6" />

      {/* Bottom goal */}
      <rect x={50 - 5.38} y={vb.h - 3} width={10.76} height="2.5" fill="url(#netPattern)" stroke={theme.lines} strokeWidth="0.8" />

      {/* Corner arcs */}
      <path d={`M 3 ${3 + 1.47} A 1.47 1.47 0 0 1 ${3 + 1.47} 3`} fill="none" stroke={theme.lines} strokeWidth="0.5" />
      <path d={`M ${97 - 1.47} 3 A 1.47 1.47 0 0 1 97 ${3 + 1.47}`} fill="none" stroke={theme.lines} strokeWidth="0.5" />
      <path d={`M ${3 + 1.47} ${vb.h - 3} A 1.47 1.47 0 0 1 3 ${vb.h - 3 - 1.47}`} fill="none" stroke={theme.lines} strokeWidth="0.5" />
      <path d={`M 97 ${vb.h - 3 - 1.47} A 1.47 1.47 0 0 1 ${97 - 1.47} ${vb.h - 3}`} fill="none" stroke={theme.lines} strokeWidth="0.5" />

      {/* Vignette */}
      <rect x="0" y="0" width={vb.w} height={vb.h} fill="url(#vignette-soccer)" />
    </g>
  );
};

// ===== BASEBALL DIAMOND =====
// ViewBox: 150x110 — field image at top 150x100, stadium elements extend below
// Uses Final Base.png as the surface texture with SVG stadium overlays
const BaseballFull = ({ theme }) => {
  const vb = { w: 150, h: 110 };

  return (
    <g>
      {/* Dark background */}
      <rect x="0" y="0" width={vb.w} height={vb.h} fill="#1c1c24" />

      {/* Field image */}
      <image href="/textures/baseball-field.png" x="0" y="0"
        width="150" height="100" preserveAspectRatio="xMidYMid slice" />

      {/* ═══ OUTFIELD WALL ═══ */}
      <path d="M 3.4 40.6 Q 23.9 4.5 74.5 1.8 Q 124.6 3.5 147.1 40.5"
        fill="none" stroke="#1a3a22" strokeWidth="2" />
      <path d="M 3.4 40.6 Q 23.9 4.5 74.5 1.8 Q 124.6 3.5 147.1 40.5"
        fill="none" stroke="#2d5a38" strokeWidth="0.4" />

      {/* ═══ SIDE WALLS — foul line edges ═══ */}
      <polyline points="3.5,40.9 27.4,60.8 58.2,85.8"
        fill="none" stroke="#444" strokeWidth="0.6" />
      <polyline points="146.8,40.6 118.5,64.6 90.4,86.6"
        fill="none" stroke="#444" strokeWidth="0.6" />

      {/* ═══ BACKSTOP ═══ */}
      <path d="M 57.8 83.5 Q 75.6 106.8 91.5 83.8"
        fill="none" stroke="#666" strokeWidth="0.8" />

      {/* ═══ DUGOUTS ═══ */}
      {/* 3B dugout (AWAY) — angled along foul line */}
      <polygon points="42.9,73.2 53.4,81.4 46.9,86.6 36.5,78.2"
        fill="#2a6b3e" stroke="#1a4a2a" strokeWidth="0.4" />
      <text x="44.9" y="80"
        textAnchor="middle" fill="rgba(255,255,255,0.75)"
        fontSize="2.2" fontWeight="bold" fontFamily="sans-serif"
        transform="rotate(-38, 44.9, 80)">AWAY</text>

      {/* 1B dugout (HOME) — angled along foul line */}
      <polygon points="96.8,81.9 106.2,73.4 112.5,80.1 102.4,88.6"
        fill="#2a6b3e" stroke="#1a4a2a" strokeWidth="0.4" />
      <text x="104.5" y="81"
        textAnchor="middle" fill="rgba(255,255,255,0.75)"
        fontSize="2.2" fontWeight="bold" fontFamily="sans-serif"
        transform="rotate(38, 104.5, 81)">HOME</text>
    </g>
  );
};

// ===== FOOTBALL FIELD =====
const FootballFull = ({ theme }) => {
  const vb = ASPECT_RATIOS.football;
  const yd = 1.875;
  const ezDepth = 10 * yd;
  const fieldStart = ezDepth;

  return (
    <g>
      {/* PNG grass texture */}
      <image href="/textures/grass.png" x="0" y="0" width={vb.w} height={vb.h} preserveAspectRatio="none" />

      {/* Broadcast lighting gradient */}
      <rect x="0" y="0" width={vb.w} height={vb.h} fill="url(#spotlight-football)" />

      {/* End zones with gradient */}
      <rect x="2" y="2" width="96" height={ezDepth - 2} fill="url(#endzoneGrad)" stroke={theme.lines} strokeWidth="0.4" filter="url(#lineGlow-football)" />
      <rect x="2" y={vb.h - ezDepth} width="96" height={ezDepth - 2} fill="url(#endzoneGrad)" stroke={theme.lines} strokeWidth="0.4" filter="url(#lineGlow-football)" />

      {/* Endzone text areas (subtle) */}
      <text x="50" y={ezDepth / 2 + 2} fill="rgba(255,255,255,0.08)" fontSize="8" fontWeight="bold" textAnchor="middle" fontFamily="sans-serif" letterSpacing="4">END ZONE</text>
      <text x="50" y={vb.h - ezDepth / 2 + 2} fill="rgba(255,255,255,0.08)" fontSize="8" fontWeight="bold" textAnchor="middle" fontFamily="sans-serif" letterSpacing="4">END ZONE</text>

      {/* Boundary */}
      <rect x="2" y="2" width="96" height={vb.h - 4} fill="none" stroke={theme.lines} strokeWidth="0.4" filter="url(#lineGlow-football)" />

      {/* Goal lines — thicker with strong glow */}
      <line x1="2" y1={fieldStart} x2="98" y2={fieldStart} stroke={theme.lines} strokeWidth="0.6" filter="url(#lineGlowStrong-football)" />
      <line x1="2" y1={vb.h - ezDepth} x2="98" y2={vb.h - ezDepth} stroke={theme.lines} strokeWidth="0.6" filter="url(#lineGlowStrong-football)" />

      {/* Yard lines every 5 yards */}
      {Array.from({ length: 21 }, (_, i) => {
        const y = fieldStart + i * 5 * yd;
        return <line key={`yl${i}`} x1="2" y1={y} x2="98" y2={y} stroke={theme.lines} strokeWidth={i % 2 === 0 ? 0.3 : 0.15} filter="url(#lineGlow-football)" />;
      })}

      {/* Yard numbers — clean with subtle glow */}
      {[10, 20, 30, 40, 50, 40, 30, 20, 10].map((num, i) => {
        const y = fieldStart + (i + 1) * 10 * yd + 5 * yd;
        return (
          <g key={`yn${i}`} filter="url(#lineGlow-football)">
            <text x="12" y={y} fill={theme.numbers} fontSize="4" fontWeight="bold" textAnchor="middle" fontFamily="sans-serif">{num}</text>
            <text x="88" y={y} fill={theme.numbers} fontSize="4" fontWeight="bold" textAnchor="middle" fontFamily="sans-serif">{num}</text>
          </g>
        );
      })}

      {/* Hash marks (NFL — precise) */}
      {Array.from({ length: 100 }, (_, i) => {
        const y = fieldStart + i * yd;
        return (
          <g key={`hm${i}`}>
            <line x1="38" y1={y} x2="40" y2={y} stroke={theme.lines} strokeWidth="0.12" />
            <line x1="60" y1={y} x2="62" y2={y} stroke={theme.lines} strokeWidth="0.12" />
            {/* Sideline hash marks */}
            <line x1="2" y1={y} x2="4" y2={y} stroke={theme.lines} strokeWidth="0.08" opacity="0.6" />
            <line x1="96" y1={y} x2="98" y2={y} stroke={theme.lines} strokeWidth="0.08" opacity="0.6" />
          </g>
        );
      })}

      {/* Goal posts with metallic look */}
      <line x1="44" y1="2" x2="56" y2="2" stroke="#dddddd" strokeWidth="0.5" />
      <line x1="50" y1="2" x2="50" y2="0.5" stroke="#dddddd" strokeWidth="0.3" />
      <line x1="44" y1={vb.h - 2} x2="56" y2={vb.h - 2} stroke="#dddddd" strokeWidth="0.5" />
      <line x1="50" y1={vb.h - 2} x2="50" y2={vb.h - 0.5} stroke="#dddddd" strokeWidth="0.3" />

      {/* Noise texture overlay */}
      <rect x="0" y="0" width={vb.w} height={vb.h} fill={theme.surface} filter="url(#noise-football)" opacity="0.03" />

      {/* Vignette overlay */}
      <rect x="0" y="0" width={vb.w} height={vb.h} fill="url(#vignette-football)" />
    </g>
  );
};

// ===== VOLLEYBALL COURT =====
const VolleyballFull = ({ theme }) => {
  const vb = ASPECT_RATIOS.volleyball;
  const m = 11.111;
  const attackDist = 3 * m;

  return (
    <g>
      {/* Out-of-bounds area */}
      <rect x="0" y="0" width={vb.w} height={vb.h} fill={theme.outOfBounds || '#1a1a22'} />

      {/* PNG court floor textures — two colors */}
      <clipPath id="courtTopClip"><rect x="5" y="5" width="90" height={vb.h / 2 - 5} /></clipPath>
      <clipPath id="courtBotClip"><rect x="5" y={vb.h / 2} width="90" height={vb.h / 2 - 5} /></clipPath>
      <image href="/textures/court-floor-blue.png" x="5" y="5" width="90" height={vb.h / 2 - 5} preserveAspectRatio="none" clipPath="url(#courtTopClip)" />
      <image href="/textures/court-floor-orange.png" x="5" y={vb.h / 2} width="90" height={vb.h / 2 - 5} preserveAspectRatio="none" clipPath="url(#courtBotClip)" />

      {/* Spotlight for depth */}
      <rect x="5" y="5" width="90" height={vb.h - 10} fill="url(#spotlight-volleyball)" />

      {/* Court boundary with glow */}
      <rect x="5" y="5" width="90" height={vb.h - 10} fill="none" stroke={theme.lines} strokeWidth="0.6" filter="url(#lineGlowStrong-volleyball)" />

      {/* Center line */}
      <line x1="5" y1={vb.h / 2} x2="95" y2={vb.h / 2} stroke={theme.lines} strokeWidth="0.5" filter="url(#lineGlow-volleyball)" />

      {/* Net with crosshatch pattern */}
      <rect x="2" y={vb.h / 2 - 1.5} width="96" height="3" fill="url(#volleyNetPattern)" opacity="0.5" />
      {/* Net top cable */}
      <line x1="2" y1={vb.h / 2 - 1.5} x2="98" y2={vb.h / 2 - 1.5} stroke="#555555" strokeWidth="0.4" />
      {/* Net bottom cable */}
      <line x1="2" y1={vb.h / 2 + 1.5} x2="98" y2={vb.h / 2 + 1.5} stroke="#555555" strokeWidth="0.25" />

      {/* Net posts — metallic */}
      <circle cx="2" cy={vb.h / 2} r="1.2" fill="#444444" stroke="#666666" strokeWidth="0.3" />
      <circle cx="98" cy={vb.h / 2} r="1.2" fill="#444444" stroke="#666666" strokeWidth="0.3" />
      <circle cx="2" cy={vb.h / 2} r="0.5" fill="#888888" />
      <circle cx="98" cy={vb.h / 2} r="0.5" fill="#888888" />

      {/* Antennas — bright red markers with glow */}
      <line x1="5" y1={vb.h / 2 - 4} x2="5" y2={vb.h / 2 + 4} stroke="#ff0000" strokeWidth="0.6" filter="url(#lineGlowStrong-volleyball)" />
      <line x1="95" y1={vb.h / 2 - 4} x2="95" y2={vb.h / 2 + 4} stroke="#ff0000" strokeWidth="0.6" filter="url(#lineGlowStrong-volleyball)" />
      {/* Antenna tip markers */}
      <circle cx="5" cy={vb.h / 2 - 4} r="0.5" fill="#ff0000" />
      <circle cx="5" cy={vb.h / 2 + 4} r="0.5" fill="#ff0000" />
      <circle cx="95" cy={vb.h / 2 - 4} r="0.5" fill="#ff0000" />
      <circle cx="95" cy={vb.h / 2 + 4} r="0.5" fill="#ff0000" />

      {/* Attack lines — red with strong glow */}
      <line x1="5" y1={vb.h / 2 - attackDist} x2="95" y2={vb.h / 2 - attackDist} stroke={theme.attackLine} strokeWidth="0.5" filter="url(#lineGlowStrong-volleyball)" />
      <line x1="5" y1={vb.h / 2 + attackDist} x2="95" y2={vb.h / 2 + attackDist} stroke={theme.attackLine} strokeWidth="0.5" filter="url(#lineGlowStrong-volleyball)" />

      {/* Zone labels — subtle watermark */}
      <text x="78" y={vb.h / 2 - attackDist - 10} fill={theme.lines} fontSize="4" opacity="0.12" textAnchor="middle" fontFamily="sans-serif">4</text>
      <text x="50" y={vb.h / 2 - attackDist - 10} fill={theme.lines} fontSize="4" opacity="0.12" textAnchor="middle" fontFamily="sans-serif">3</text>
      <text x="22" y={vb.h / 2 - attackDist - 10} fill={theme.lines} fontSize="4" opacity="0.12" textAnchor="middle" fontFamily="sans-serif">2</text>
      <text x="78" y={vb.h / 2 - 8} fill={theme.lines} fontSize="4" opacity="0.12" textAnchor="middle" fontFamily="sans-serif">5</text>
      <text x="50" y={vb.h / 2 - 8} fill={theme.lines} fontSize="4" opacity="0.12" textAnchor="middle" fontFamily="sans-serif">6</text>
      <text x="22" y={vb.h / 2 - 8} fill={theme.lines} fontSize="4" opacity="0.12" textAnchor="middle" fontFamily="sans-serif">1</text>
      <text x="22" y={vb.h / 2 + attackDist + 14} fill={theme.lines} fontSize="4" opacity="0.12" textAnchor="middle" fontFamily="sans-serif">4</text>
      <text x="50" y={vb.h / 2 + attackDist + 14} fill={theme.lines} fontSize="4" opacity="0.12" textAnchor="middle" fontFamily="sans-serif">3</text>
      <text x="78" y={vb.h / 2 + attackDist + 14} fill={theme.lines} fontSize="4" opacity="0.12" textAnchor="middle" fontFamily="sans-serif">2</text>
      <text x="22" y={vb.h / 2 + 12} fill={theme.lines} fontSize="4" opacity="0.12" textAnchor="middle" fontFamily="sans-serif">5</text>
      <text x="50" y={vb.h / 2 + 12} fill={theme.lines} fontSize="4" opacity="0.12" textAnchor="middle" fontFamily="sans-serif">6</text>
      <text x="78" y={vb.h / 2 + 12} fill={theme.lines} fontSize="4" opacity="0.12" textAnchor="middle" fontFamily="sans-serif">1</text>

      {/* Noise texture overlay */}
      <rect x="0" y="0" width={vb.w} height={vb.h} fill={theme.surfaceLeft} filter="url(#noise-volleyball)" opacity="0.03" />

      {/* Vignette overlay */}
      <rect x="0" y="0" width={vb.w} height={vb.h} fill="url(#vignette-volleyball)" />
    </g>
  );
};

// ===== SURFACE REGISTRY =====
const SURFACE_RENDERERS = {
  basketball: { half: BasketballHalf, full: BasketballFull },
  soccer: { full: SoccerFull, half: SoccerFull },
  baseball: { 'full-field': BaseballFull, infield: BaseballFull, diamond: BaseballFull },
  football: { full: FootballFull, 'red-zone': FootballFull, 'goal-line': FootballFull, formation: FootballFull },
  volleyball: { full: VolleyballFull, 'half-offense': VolleyballFull, 'half-defense': VolleyballFull }
};

/**
 * PlaySurfaceRenderer — Universal sport surface renderer
 * Broadcast-quality SVG playing surfaces with textures, gradients, filters,
 * and premium visual fidelity for any sport with correct proportions.
 */
const PlaySurfaceRenderer = ({
  sport = 'basketball',
  view = 'half',
  orientation = 'vertical',
  width,
  height,
  customTheme = null,
  showGrid = false,
  onClick,
  children
}) => {
  const theme = useMemo(() => {
    const base = SURFACE_THEMES[sport] || SURFACE_THEMES.basketball;
    return customTheme ? { ...base, ...customTheme } : base;
  }, [sport, customTheme]);

  const SurfaceComponent = useMemo(() => {
    const sportRenderers = SURFACE_RENDERERS[sport] || SURFACE_RENDERERS.basketball;
    return sportRenderers[view] || Object.values(sportRenderers)[0];
  }, [sport, view]);

  // Get the correct aspect ratio for this sport/view
  const aspectKey = sport === 'basketball' && view === 'half' ? 'basketballHalf' : sport;
  const aspect = ASPECT_RATIOS[aspectKey] || ASPECT_RATIOS.basketballHalf;

  const isHorizontal = orientation === 'horizontal';

  const viewBox = `0 0 ${aspect.w} ${aspect.h}`;

  // Calculate display dimensions preserving aspect ratio
  const aspectRatio = aspect.w / aspect.h;
  let displayW = width;
  let displayH = height;
  if (width && !height) {
    displayH = isHorizontal ? width * aspectRatio : width / aspectRatio;
  } else if (height && !width) {
    displayW = isHorizontal ? height / aspectRatio : height * aspectRatio;
  } else if (!width && !height) {
    displayW = 400;
    displayH = isHorizontal ? 400 * aspectRatio : 400 / aspectRatio;
  }

  const transform = isHorizontal ? `rotate(-90 ${aspect.w / 2} ${aspect.h / 2})` : undefined;

  return (
    <svg
      viewBox={viewBox}
      width={displayW}
      height={displayH}
      style={{ userSelect: 'none', touchAction: 'none', maxWidth: '100%', height: 'auto' }}
      onClick={onClick}
    >
      <SharedDefs sport={sport} vbW={aspect.w} vbH={aspect.h} />
      <g transform={transform}>
        <rect x="0" y="0" width={aspect.w} height={aspect.h} fill={theme.outOfBounds || '#1a1a22'} />
        <SurfaceComponent theme={theme} />
        {showGrid && (() => {
          // Grid labels show data-space coordinates (0-100) regardless of viewBox aspect ratio
          // Grid lines are placed at data-space intervals scaled to viewBox
          const scaleY = aspect.h / 100;
          return (
            <g>
              {/* Grid lines — 10-unit data-space intervals */}
              <g opacity="0.15">
                {Array.from({ length: 11 }, (_, i) => (
                  <g key={`grid-${i}`}>
                    <line x1={i * 10} y1="0" x2={i * 10} y2={aspect.h} stroke="#fff" strokeWidth="0.15" />
                    <line x1="0" y1={i * 10 * scaleY} x2={aspect.w} y2={i * 10 * scaleY} stroke="#fff" strokeWidth="0.15" />
                  </g>
                ))}
              </g>
              {/* Coordinate labels — data-space values (0-100) */}
              {Array.from({ length: 11 }, (_, i) => (
                <g key={`label-${i}`}>
                  <text
                    x={i * 10} y={aspect.h - 0.5}
                    textAnchor="middle" fill="rgba(255,255,255,0.35)"
                    fontSize="1.5" fontFamily="monospace"
                  >{i * 10}</text>
                  <text
                    x={1} y={i * 10 * scaleY + 1.5}
                    textAnchor="start" fill="rgba(255,255,255,0.35)"
                    fontSize="1.5" fontFamily="monospace"
                  >{i * 10}</text>
                </g>
              ))}
            </g>
          );
        })()}
        {children}
      </g>
    </svg>
  );
};

export { SURFACE_THEMES, SURFACE_RENDERERS, ASPECT_RATIOS };
export default PlaySurfaceRenderer;
